<template>
  <section class="pa-4 pa-md-8">
    <div>
      <t-page-title title="Check-in de Usuários" />

      <v-select
        v-if="smAndDown"
        v-model="status"
        class="mb-3"
        variant="outlined"
        density="compact"
        hide-details
        :items="statusItems"
        @update:model-value="search"
      />

      <v-card class="pa-6 mb-3">
        <v-row>
          <v-col cols="12">
            <v-select
              v-model="events.model"
              variant="outlined"
              label="Evento"
              :items="events.items"
              density="compact"
              hide-details
              :disabled="loader.loading"
              @update:model-value="search"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="cpf"
              v-maska="$masks.cpf.opts.mask"
              label="Buscar por CPF"
              prepend-inner-icon="fa-solid fa-id-card"
              variant="outlined"
              density="compact"
              hide-details
              @keydown.enter="search"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="name"
              label="Buscar por Nome"
              prepend-inner-icon="fa-solid fa-user"
              variant="outlined"
              density="compact"
              hide-details
              @keydown.enter="search"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="email"
              label="Buscar por E-mail"
              prepend-inner-icon="fa-solid fa-envelope"
              variant="outlined"
              density="compact"
              hide-details
              @keydown.enter="search"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn
              color="primary"
              block
              :loading="loader.loading"
              @click="search"
            >
              <v-icon start icon="fa-solid fa-magnifying-glass" />
              Pesquisar
            </v-btn>
          </v-col>
        </v-row>
        <v-row />
      </v-card>

      <v-tabs
        v-if="!smAndDown"
        v-model="status"
        color="primary"
        @update:model-value="search"
      >
        <v-tab value="all">
          Todos os usuários
        </v-tab>
        <v-tab value="checked_in">
          Já validados
        </v-tab>
        <v-tab value="pending">
          Falta validar
        </v-tab>
      </v-tabs>
    </div>

    <div>
      <data-table
        v-show="!smAndDown"
        ref="datatable"
        :headers="headers"
        :provider="fetch"
        :filters="[]"
        hide-items-per-page
      >
        <template #[`item.document`]="{ item }">
          <span>{{ formatDocument(item.document, item.document_type) }}</span>
        </template>

        <template #[`item.passport.reading`]="{ item }">
          <v-chip :color="item.passport?.reading ? 'green' : 'orange'" size="small">
            {{ item.passport?.reading ? 'Validado' : 'Pendente' }}
          </v-chip>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            v-if="!item.passport?.reading"
            color="primary"
            size="small"
            prepend-icon="fa-solid fa-check"
            :disabled="!can('create', 'readings')"
            @click="doCheckIn(item)"
          >
            Check-in
          </v-btn>
          <v-btn
            v-else
            variant="outlined"
            size="small"
            prepend-icon="fa-solid fa-rotate-left"
            :disabled="!can('delete', 'readings')"
            @click="doUndo(item)"
          >
            Desfazer
          </v-btn>
        </template>
      </data-table>

      <div v-if="smAndDown">
        <v-card
          v-for="item in mobileItems"
          :key="item.id"
          class="pa-4 mb-3"
        >
          <div class="text-caption text-medium-emphasis">
            {{ item.code }}
          </div>
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="font-weight-bold">{{ item.name }}</span>
            <v-chip :color="item.passport?.reading ? 'green' : 'orange'" size="small">
              {{ item.passport?.reading ? 'Validado' : 'Pendente' }}
            </v-chip>
          </div>
          <div class="text-body-2">
            {{ formatDocument(item.document, item.document_type) }}
          </div>
          <div class="text-body-2 mb-3">
            {{ item.email }}
          </div>
          <div v-if="item.courtesy_name" class="text-body-2 mb-3">
            <strong>Cortesia:</strong> {{ item.courtesy_name }}
          </div>
          <v-btn
            v-if="!item.passport?.reading"
            color="primary"
            block
            prepend-icon="fa-solid fa-check"
            :disabled="!can('create', 'readings')"
            @click="doCheckIn(item)"
          >
            Check-in
          </v-btn>
          <v-btn
            v-else
            variant="outlined"
            block
            prepend-icon="fa-solid fa-rotate-left"
            :disabled="!can('delete', 'readings')"
            @click="doUndo(item)"
          >
            Desfazer
          </v-btn>
        </v-card>

        <v-pagination
          v-if="mobilePagination.totalPages > 1"
          v-model="mobilePagination.page"
          class="py-4"
          total-visible="5"
          :length="mobilePagination.totalPages"
          density="compact"
          prev-icon="fa-solid fa-angle-left"
          next-icon="fa-solid fa-angle-right"
          @update:model-value="(p: number) => datatable?.fetch(p)"
        />
      </div>
    </div>

    <v-card
      class="checkin-footer mt-4 pa-3 rounded-t-xl rounded-b-0"
      color="primary"
      elevation="12"
    >
      <div class="d-flex align-stretch">
        <div class="d-flex flex-column align-center flex-grow-1 ga-1">
          <div class="text-h6 font-weight-bold" style="line-height: 1.2">
            {{ stats.total }}
          </div>
          <div class="text-caption text-white text-center" style="opacity: 0.75; line-height: 1.2">
            {{ smAndDown ? 'Total' : 'Total de usuários' }}
          </div>
        </div>

        <v-divider vertical class="mx-2" style="opacity: 0.25" />

        <div class="d-flex flex-column align-center flex-grow-1 ga-1">
          <div class="text-h6 font-weight-bold" style="line-height: 1.2">
            {{ stats.checked_in }}
          </div>
          <div class="text-caption text-white text-center" style="opacity: 0.75; line-height: 1.2">
            Validados
          </div>
        </div>

        <v-divider vertical class="mx-2" style="opacity: 0.25" />

        <div class="d-flex flex-column align-center flex-grow-1 ga-1">
          <div class="text-h6 font-weight-bold" style="line-height: 1.2">
            {{ stats.pending }}
          </div>
          <div class="text-caption text-white text-center" style="opacity: 0.75; line-height: 1.2">
            Pendentes
          </div>
        </div>
      </div>
    </v-card>
  </section>
</template>

<script lang="ts" setup>
import { ref, useTemplateRef } from 'vue'
import { useDisplay } from 'vuetify'
import { useAbility } from '@casl/vue'

import TPageTitle from '@/components/TPageTitle.vue'
import DataTable from '@/components/datatable/DataTable.vue'
import CheckInService from '@/services/CheckInService'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { formatDocument } from '@/helpers/formatters/document'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import { useProducerStore } from '@/stores/producer'
import type { DataTableHeader } from '@/types/DataTable'
import type OrderItem from '@/types/OrderItem'

const { can } = useAbility()
const loader = useLoader()
const snackbar = useSnackBar()
const { smAndDown } = useDisplay()

const datatable = useTemplateRef('datatable')

const producer = ref(useProducerStore().producer!)

const events = ref(useEventFilter({
  producer: producer.value,
  label: 'Evento',
  field: 'event_id',
  type: 'select',
}))

const cpf = ref('')
const name = ref('')
const email = ref('')
const status = ref<'all' | 'checked_in' | 'pending'>('all')

const statusItems = [
  { title: 'Todos os usuários', value: 'all' },
  { title: 'Já validados', value: 'checked_in' },
  { title: 'Falta validar', value: 'pending' },
]

const stats = ref({ total: 0, checked_in: 0, pending: 0 })

const mobileItems = ref<OrderItem[]>([])
const mobilePagination = ref({ page: 1, totalPages: 1 })

const headers: DataTableHeader[] = [
  { key: 'code', value: 'code', title: 'Código de Barras', sortable: false },
  { key: 'name', value: 'name', title: 'Nome completo', sortable: false },
  { key: 'document', value: 'document', title: 'CPF', sortable: false },
  { key: 'email', value: 'email', title: 'E-mail', sortable: false },
  { key: 'courtesy_name', value: 'courtesy_name', title: 'Nome da Cortesia', sortable: false },
  { key: 'passport.reading', value: 'passport.reading', title: 'Status', sortable: false, align: 'center' },
  { key: 'actions', value: '', title: 'Ação', sortable: false, align: 'center' },
]

function search() {
  datatable.value?.fetch(1)
}

async function fetch(page: number, _filters: string, per_page?: number) {
  await events.value.ready

  if (!events.value.model) {
    snackbar.show('Não há eventos disponíveis para realizar o check-in.', 'info')
    throw new Error('No events available')
  }

  try {
    const params = new URLSearchParams()
    if (cpf.value) params.set('holder_document', cpf.value)
    if (name.value) params.set('holder_name', name.value)
    if (email.value) params.set('holder_email', email.value)
    if (status.value !== 'all') params.set('status', status.value)

    const response = await new CheckInService().index(events.value.model, page, params.toString(), per_page)

    const additional = response.data.additional as { total?: number, checked_in?: number, pending?: number } | undefined

    stats.value = {
      total: additional?.total ?? 0,
      checked_in: additional?.checked_in ?? 0,
      pending: additional?.pending ?? 0,
    }

    const meta = response.data.meta ?? response.data

    mobileItems.value = response.data.data
    mobilePagination.value = {
      page: meta.current_page,
      totalPages: meta.last_page,
    }

    return response
  } catch (error) {
    snackbar.show('Não foi possível buscar os usuários', 'error')

    throw error
  }
}

async function doCheckIn(item: OrderItem) {
  if (!events.value.model) return

  try {
    loader.increment()
    await new CheckInService().checkIn(events.value.model, item.id)
    snackbar.show('Check-in realizado com sucesso.', 'success')
    datatable.value?.fetch(1)
  } catch {
    snackbar.show('Não foi possível realizar o check-in.', 'error')
  } finally {
    loader.decrement()
  }
}

async function doUndo(item: OrderItem) {
  if (!events.value.model) return

  try {
    loader.increment()
    await new CheckInService().undoCheckIn(events.value.model, item.id)
    snackbar.show('Check-in desfeito com sucesso.', 'success')
    datatable.value?.fetch(1)
  } catch {
    snackbar.show('Não foi possível desfazer o check-in.', 'error')
  } finally {
    loader.decrement()
  }
}
</script>

<style scoped>
.checkin-footer {
  position: sticky;
  bottom: 0;
  z-index: 1;
}
</style>
